import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from 'lib/supabase';
import { useApp } from 'contexts/AppContext';
import { useLeague } from 'contexts/LeagueContext';

import NavigationBar from "components/ui/NavigationBar";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import PlayerTable from "./components/PlayerTable";
import PlayerModal from "./components/PlayerModal";
import DeleteConfirmModal from "./components/DeleteConfirmModal";
import SummaryPanel from "./components/SummaryPanel";
import SearchBar from "./components/SearchBar";

const PlayersManagement = () => {
  const navigate = useNavigate();
  const { players, playersLoading, playersError, fetchPlayers, getPlayerStats } = useApp();
  const { activeLeague, isAdmin } = useLeague();
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [modalState, setModalState] = useState({ isOpen: false, mode: "add", player: null });
  const [deleteState, setDeleteState] = useState({ isOpen: false, player: null });
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev?.key === key && prev?.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Enrich players with computed stats from context
  const enrichedPlayers = useMemo(() => {
    return players?.map(p => {
      const s = getPlayerStats(p?.id) || {};
      return {
        ...p,
        number: p?.number || '',
        email: p?.email || '',
        phone: p?.phone || '',
        avatar: p?.avatar_url || '',
        avatarAlt: `${p?.name} player avatar`,
        addedDate: p?.created_at?.split('T')?.[0] || '',
        gamesPlayed: s.gamesPlayed || 0,
        winPct: s.winPct || 0,
        ppg: s.avgPoints || 0,
        rpg: s.avgRebounds || 0,
        apg: s.avgAssists || 0,
      };
    });
  }, [players, getPlayerStats]);

  const filteredSorted = useMemo(() => {
    let result = enrichedPlayers?.filter((p) =>
      p?.name?.toLowerCase()?.includes(search?.toLowerCase()) ||
      p?.position?.toLowerCase()?.includes(search?.toLowerCase())
    );
    result = [...result]?.sort((a, b) => {
      const aVal = a?.[sortConfig?.key];
      const bVal = b?.[sortConfig?.key];
      const dir = sortConfig?.direction === "asc" ? 1 : -1;
      if (typeof aVal === "string") return aVal?.localeCompare(bVal) * dir;
      return (aVal - bVal) * dir;
    });
    return result;
  }, [enrichedPlayers, search, sortConfig]);

  const handleSave = async (formData) => {
    if (!supabase) { showToast('Database not connected. Please configure Supabase.', 'error'); setModalState({ isOpen: false, mode: "add", player: null }); return; }
    setSaving(true);
    try {
      if (modalState?.mode === "edit") {
        const { error } = await supabase.from('players').update({ name: formData?.name, number: formData?.number ?? null, position: formData?.position, avatar_url: formData?.avatar || '' }).eq('id', modalState?.player?.id);
        if (error) throw error;
        showToast(`${formData?.name} updated successfully`);
      } else {
        const { error } = await supabase.from('players').insert({ name: formData?.name, number: formData?.number ?? null, position: formData?.position, avatar_url: formData?.avatar || '', league_id: activeLeague?.id });
        if (error) throw error;
        showToast(`${formData?.name} added to roster!`);
      }
      await fetchPlayers();
    } catch (err) {
      showToast(err?.message || 'Failed to save player', 'error');
    } finally {
      setSaving(false);
      setModalState({ isOpen: false, mode: "add", player: null });
    }
  };

  const handleDelete = async (id) => {
    if (!supabase) { showToast('Database not connected. Please configure Supabase.', 'error'); return; }
    const player = players?.find((p) => p?.id === id);
    try {
      const { error } = await supabase.from('players').delete().eq('id', id);
      if (error) throw error;
      await fetchPlayers();
      showToast(`${player?.name} removed from roster`, "success");
    } catch (err) {
      showToast(err?.message || 'Failed to delete player', 'error');
    }
    setDeleteState({ isOpen: false, player: null });
  };

  const handleViewProfile = (player) => {
    if (player?.id) navigate(`/players/${player.id}`);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--color-background)" }}>
      <NavigationBar />
      <main className="main-content">
        <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-xl mx-auto">
          {/* Page Header */}
          {!isAdmin && (
            <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-200">
              <Icon name="Info" size={16} color="#2563EB" />
              <span className="font-caption text-sm" style={{ color: '#2563EB' }}>You have view-only access. Only league admins can manage players.</span>
            </div>
          )}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: "var(--color-foreground)" }}>Players Management</h1>
              <p className="font-caption text-sm mt-1" style={{ color: "var(--color-muted-foreground)" }}>Manage your basketball roster — family &amp; friends</p>
            </div>
            {isAdmin && (
              <Button variant="default" iconName="UserPlus" iconPosition="left" onClick={() => setModalState({ isOpen: true, mode: "add", player: null })}>
                Add New Player
              </Button>
            )}
          </div>

          {/* Error State */}
          {playersError && (
            <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-xl" style={{ backgroundColor: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)' }}>
              <Icon name="AlertCircle" size={16} color="#DC2626" />
              <span className="font-caption text-sm" style={{ color: '#DC2626' }}>{playersError}</span>
              <button onClick={fetchPlayers} className="ml-auto font-caption text-xs underline" style={{ color: '#DC2626' }}>Retry</button>
            </div>
          )}

          {/* Loading State */}
          {playersLoading && (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-primary)' }} />
                <p className="font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>Loading players...</p>
              </div>
            </div>
          )}

          {!playersLoading && (
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left: Table */}
              <div className="flex-1 min-w-0">
                <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--color-card)", borderColor: "var(--color-border)", boxShadow: "var(--shadow-md)" }}>
                  <div className="px-4 md:px-6 py-4" style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <SearchBar value={search} onChange={setSearch} resultCount={filteredSorted?.length} totalCount={enrichedPlayers?.length} />
                  </div>
                  <PlayerTable
                    players={filteredSorted}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onEdit={isAdmin ? (p) => setModalState({ isOpen: true, mode: "edit", player: p }) : null}
                    onDelete={isAdmin ? (p) => setDeleteState({ isOpen: true, player: p }) : null}
                    onViewProfile={handleViewProfile}
                  />
                  <div className="px-4 md:px-6 py-3 flex items-center justify-between" style={{ borderTop: "1px solid var(--color-border)", backgroundColor: "var(--color-muted)" }}>
                    <p className="font-caption text-xs" style={{ color: "var(--color-muted-foreground)" }}>
                      {filteredSorted?.length} player{filteredSorted?.length !== 1 ? "s" : ""} shown
                    </p>
                    <button
                      className="flex items-center gap-1.5 font-caption text-xs font-medium transition-athletic hover:opacity-80"
                      style={{ color: "var(--color-primary)" }}
                      onClick={() => navigate("/game-recording")}>
                      <Icon name="ClipboardList" size={13} color="currentColor" />
                      Record a Game
                    </button>
                  </div>
                </div>
              </div>

              {/* Right: Summary Panel */}
              <div className="w-full lg:w-64 xl:w-72 flex-shrink-0">
                <SummaryPanel players={enrichedPlayers} />
              </div>
            </div>
          )}
        </div>
      </main>

      <PlayerModal
        isOpen={modalState?.isOpen}
        mode={modalState?.mode}
        player={modalState?.player}
        onClose={() => setModalState({ isOpen: false, mode: "add", player: null })}
        onSave={handleSave}
        saving={saving}
      />
      <DeleteConfirmModal
        isOpen={deleteState?.isOpen}
        player={deleteState?.player}
        onClose={() => setDeleteState({ isOpen: false, player: null })}
        onConfirm={handleDelete}
      />

      {toast && (
        <div className="fixed bottom-20 lg:bottom-6 right-4 lg:right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl font-caption text-sm text-white transition-all" style={{ backgroundColor: toast?.type === "error" ? "var(--color-error)" : "var(--color-success)", minWidth: "220px" }}>
          <Icon name={toast?.type === "error" ? "Trash2" : "CheckCircle"} size={16} color="white" />
          {toast?.message}
        </div>
      )}
    </div>
  );
};

export default PlayersManagement;